import Phaser from 'phaser';
import { CANVAS_W, CANVAS_H } from '../types';
import { GameSceneData } from './GameScene';
import { FINAL_LINES, interpolate } from '../config/story';
import { AudioManager } from '../systems/AudioManager';
import { clearAutosave, saveHighScore } from '../systems/SaveManager';

const TYPE_SPEED_MS = 40;

export class VictoryScene extends Phaser.Scene {
  private payload!: GameSceneData;
  private lines: string[] = [];
  private lineIdx = 0;
  private charIdx = 0;
  private body!: Phaser.GameObjects.Text;
  private done = false;
  private leavingAt = 0;
  private princess!: Phaser.GameObjects.Sprite;
  private geo!: Phaser.GameObjects.Sprite;
  private aris!: Phaser.GameObjects.Sprite;

  constructor() { super({ key: 'VictoryScene' }); }

  init(data: GameSceneData) {
    this.payload = data;
  }

  create() {
    this.cameras.main.setBackgroundColor('#131120');
    // Save final score + clear autosave so user can start a new run
    const finalScore = Math.max(this.payload.p1.score, this.payload.p2.score);
    saveHighScore(finalScore);
    clearAutosave();

    AudioManager.sfx('level-win');

    // Title
    this.add.text(CANVAS_W / 2, 40, '🎉 BOMBASTIK 🎉', {
      fontFamily: 'monospace', fontSize: '36px', color: '#ffd166',
    }).setOrigin(0.5);
    this.add.text(CANVAS_W / 2, 78, 'Prințesa Elevarta a fost salvată', {
      fontFamily: 'monospace', fontSize: '14px', color: '#06d6a0',
    }).setOrigin(0.5);

    // Characters reunion (Geo · Princess · Aris)
    const centerY = 170;
    const labelY  = centerY + 110;
    this.geo = this.add.sprite(CANVAS_W / 2 - 150, centerY, 'player-geo-side');
    this.geo.setFlipX(true);
    this.fitSprite(this.geo, 100);
    this.add.text(CANVAS_W / 2 - 150, labelY, this.payload.p1.name, {
      fontFamily: 'monospace', fontSize: '13px', color: '#e63946',
    }).setOrigin(0.5);

    this.princess = this.add.sprite(CANVAS_W / 2, centerY, 'princess');
    this.fitSprite(this.princess, 130);
    this.add.text(CANVAS_W / 2, labelY, 'PRINȚESA', {
      fontFamily: 'monospace', fontSize: '13px', color: '#862d91',
    }).setOrigin(0.5);

    this.aris = this.add.sprite(CANVAS_W / 2 + 150, centerY, 'player-aris-side');
    this.aris.setFlipX(false);
    this.fitSprite(this.aris, 100);
    this.add.text(CANVAS_W / 2 + 150, labelY, this.payload.p2.name, {
      fontFamily: 'monospace', fontSize: '13px', color: '#52b788',
    }).setOrigin(0.5);

    // Gentle bobbing
    [this.geo, this.princess, this.aris].forEach((s, i) => {
      this.tweens.add({
        targets: s,
        y: s.y - 6,
        yoyo: true,
        repeat: -1,
        duration: 700 + i * 60,
        ease: 'Sine.easeInOut',
      });
    });

    // Sparkles around the princess — tight ring so they don't clash with labels
    for (let i = 0; i < 10; i++) {
      const ang = (i / 10) * Math.PI * 2;
      const r = 70;
      const px = this.princess.x + Math.cos(ang) * r;
      const py = this.princess.y + Math.sin(ang) * r;
      const star = this.add.text(px, py, '✨', { fontSize: '16px' }).setOrigin(0.5).setAlpha(0);
      this.tweens.add({
        targets: star, alpha: { from: 0.0, to: 0.9 }, yoyo: true, repeat: -1,
        duration: 800, delay: i * 80, ease: 'Sine.easeInOut',
      });
    }

    // Story body underneath — below labels, above scores
    this.lines = FINAL_LINES.map(l => interpolate(l, this.payload.p1.name, this.payload.p2.name));
    this.body = this.add.text(CANVAS_W / 2, labelY + 30, '', {
      fontFamily: 'monospace',
      fontSize: '15px',
      color: '#fff',
      align: 'center',
      wordWrap: { width: CANVAS_W - 80 },
      lineSpacing: 6,
    }).setOrigin(0.5, 0);

    // Final scores — anchored to bottom
    const scoresText = `${this.payload.p1.name}: ${this.payload.p1.score.toString().padStart(6, '0')}   ${this.payload.p2.name}: ${this.payload.p2.score.toString().padStart(6, '0')}`;
    this.add.text(CANVAS_W / 2, CANVAS_H - 56, scoresText, {
      fontFamily: 'monospace', fontSize: '15px', color: '#ffd166',
    }).setOrigin(0.5);

    this.add.text(CANVAS_W / 2, CANVAS_H - 24, 'SPACE / ENTER = înapoi la meniu', {
      fontFamily: 'monospace', fontSize: '10px', color: '#666',
    }).setOrigin(0.5);

    // Start typing
    this.time.delayedCall(600, () => this.startTyping());

    const kb = this.input.keyboard!;
    const onAdvance = () => this.handleAdvance();
    kb.on('keydown-SPACE', onAdvance);
    kb.on('keydown-ENTER', onAdvance);
    kb.on('keydown-ESC',   () => this.goToMenu());
    this.input.on('pointerdown', onAdvance);
  }

  private fitSprite(s: Phaser.GameObjects.Sprite, targetPx: number) {
    const src = s.texture.getSourceImage() as HTMLImageElement;
    const longest = Math.max(src.width || 1, src.height || 1);
    s.setScale(targetPx / longest);
  }

  private startTyping() {
    this.time.addEvent({
      delay: TYPE_SPEED_MS,
      loop: true,
      callback: () => this.tick(),
    });
  }

  private tick() {
    if (this.done) return;
    if (this.lineIdx >= this.lines.length) { this.done = true; return; }
    const line = this.lines[this.lineIdx];
    if (this.charIdx < line.length) {
      this.body.setText(this.body.text + line.charAt(this.charIdx));
      this.charIdx++;
    } else {
      this.body.setText(this.body.text + '\n');
      this.lineIdx++;
      this.charIdx = 0;
    }
  }

  private handleAdvance() {
    if (this.leavingAt > 0) return; // input locked while we transition out
    if (this.done) { this.goToMenu(); return; }
    // Skip typing
    this.body.setText(this.lines.join('\n'));
    this.done = true;
  }

  private goToMenu() {
    if (this.leavingAt > 0) return;
    this.leavingAt = this.time.now;
    AudioManager.sfx('menu-select');
    // Detach keyboard listeners IMMEDIATELY so any further ENTER/SPACE during
    // the transition window cannot fire handleAdvance on this scene.
    this.input.keyboard?.removeAllListeners();
    this.input.removeAllListeners();
    // Delay swallows leftover ENTER/SPACE before MenuScene grabs focus.
    this.time.delayedCall(260, () => this.scene.start('MenuScene'));
  }
}
